import { AsyncPipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  effect,
  ElementRef,
  inject,
  Injector,
  input,
  OnInit,
  output,
  viewChild,
} from '@angular/core';
import { FormControl, ReactiveFormsModule, UntypedFormControl } from '@angular/forms';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatMenuModule, MatMenuTrigger } from '@angular/material/menu';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { format, parse } from 'date-fns';
import { BehaviorSubject, map, Observable, of } from 'rxjs';

import { DatasetColumn } from '@blaze-shared/schema-domain';
import {
  timeInputBlurFn,
  timeInputPasteFn,
  timeInputTransformFn,
} from '@blaze-shared/shared-feature';
import { ScrollerComponent } from '@blaze-shared/shared-ui';
import {
  isValidDate,
  Meridian,
  regexpMatch,
  removeSeconds,
  TIME_PROPOSAL_ARRAY,
  timeFormatter,
  timeParsingFormats,
  timeRegex,
} from '@blaze-shared/shared-util';

@UntilDestroy()
@Component({
  selector: 'blaze-cell-time',
  templateUrl: './cell-time.component.html',
  styleUrls: ['./cell-time.component.scss', '../../base-cell-input.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ReactiveFormsModule, MatMenuModule, MatDatepickerModule, ScrollerComponent, AsyncPipe],
})
export class CellTimeComponent implements OnInit {
  injector = inject(Injector);

  column = input.required<DatasetColumn>();
  rowNum = input.required<number>();
  value = input<unknown>();
  isDisabled = input<boolean>(false);

  pickerClosed = output();
  pickerOpened = output();
  valueChanged = output<string | null>();

  timeInputRef = viewChild('timeInput', { read: ElementRef });
  timeMenuTrigger = viewChild('timeMenuTrigger', { read: MatMenuTrigger });

  protected timeProposalArray$: Observable<string[]> = of([]);
  protected filterString$ = new BehaviorSubject('');
  protected useTwelveHoursTimeFormat = true;
  protected timeSuffix: Meridian = '';

  protected displayedTimeFormControl: FormControl = new FormControl();

  protected get nativeElement(): HTMLInputElement | null {
    return this.timeInputRef()?.nativeElement || null;
  }

  protected get pickedTimeForSelectedMarker(): string {
    return this.displayedTimeFormControl.value + ' ' + this.timeSuffix;
  }

  ngOnInit(): void {
    effect(
      () => {
        if (!this.value()) {
          this.displayedTimeFormControl.patchValue(null, { emitEvent: false });
        }
      },
      { injector: this.injector },
    );
    this.displayedTimeFormControl = this.createTimeFormControl();
    this.timeProposalArray$ = this.filterString$.pipe(
      map((input) => {
        if (!input) {
          return TIME_PROPOSAL_ARRAY;
        }

        const baseTime = TIME_PROPOSAL_ARRAY.filter((time) =>
          time.toLowerCase().startsWith(input.toLowerCase()),
        );
        return baseTime.length ? baseTime : this.proposeValuesFromInput(input);
      }),
    );
  }

  openTimePicker(): void {
    const focusInput = !this.timeMenuTrigger()?.menuOpen;
    this.timeMenuTrigger()?.openMenu();
    setTimeout(() => {
      this.timeInputRef()?.nativeElement?.focus();
      if (focusInput) {
        this.timeInputRef()?.nativeElement?.select();
      }
      this.pickerOpened.emit();
    });
  }

  closeTimePicker(): void {
    this.timeMenuTrigger()?.closeMenu();
    this.filterString$.next('');
  }

  protected moveCarriage(step: number | 'end', input: HTMLInputElement): void {
    if (step === 'end') {
      input.selectionStart = input.selectionEnd;
    } else if (input.selectionEnd === input.selectionStart) {
      const newIndex = (input.selectionEnd || 0) + step;
      input.selectionEnd = newIndex;
      input.selectionStart = newIndex;
    }
  }

  protected timeInputTransform(event: InputEvent, formControl: FormControl): void {
    timeInputTransformFn(
      event,
      formControl,
      this.useTwelveHoursTimeFormat,
      this.useTwelveHoursTimeFormat,
    );
  }

  protected timeInputKeyDown(event: KeyboardEvent): void {
    // BL-7853 - Temporary solution
    // Pass through only specific events so the FocusKeyManager does not handle every keydown event
    // and take focus, blurring the input in effect.
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      return;
    }

    event.stopPropagation();
  }

  protected timeInputPaste(event: ClipboardEvent, formControl: UntypedFormControl): void {
    const changeTimeVariantToPM = timeInputPasteFn(
      event,
      formControl,
      this.useTwelveHoursTimeFormat,
    );
    if (changeTimeVariantToPM) {
      this.timeSuffix = 'PM';
    }
  }

  protected timeInputBlur(event: FocusEvent, formControl: UntypedFormControl): void {
    // omit blur effect when clicked on proposed time from menu
    if ((event.relatedTarget as HTMLElement)?.closest('.inline-editor-time-picker')) {
      return;
    } else {
      this.closeTimePicker();
    }
    timeInputBlurFn(formControl, this.useTwelveHoursTimeFormat);
  }

  protected patchTimeProposal(value: string): void {
    this.displayedTimeFormControl.patchValue(value);
  }

  private proposeValuesFromInput(value: string | undefined): string[] {
    let newVal = value || this.timeInputRef()?.nativeElement?.value || '';
    const formControl = new UntypedFormControl();
    formControl.setValue(newVal);
    timeInputBlurFn(formControl, this.useTwelveHoursTimeFormat);
    if (!formControl.value) {
      return [];
    }
    newVal = formControl.value.replaceAll(/[APM\s]/gim, '');
    const timeWithSuffix = [];
    if (Number(newVal.split(':')[0]) > 12) {
      timeWithSuffix.push(newVal);
    } else {
      timeWithSuffix.push(`${newVal} AM`, `${newVal} PM`);
    }
    return timeWithSuffix;
  }

  private getTimeParts(
    newTime: string | null,
  ): { hours: string; minutes: string; ampm: string } | null {
    if (!newTime) {
      return null;
    }
    let parsed: Date;

    if (this.useTwelveHoursTimeFormat) {
      parsed = parse(
        `${newTime || '00:00 AM'}`,
        timeParsingFormats.timeInputTwelveHoursWithSuffix,
        new Date(),
      );
    } else {
      parsed = parse(`${newTime || '00:00'}`, timeParsingFormats.timeInput, new Date());
    }

    if (!isValidDate(parsed)) {
      const parsedFallbackParts = newTime.split(/:|\s/);
      return {
        hours: parsedFallbackParts[0],
        minutes: parsedFallbackParts[1],
        ampm: parsedFallbackParts[2],
      };
    }

    return {
      hours: format(parsed, 'HH'),
      minutes: format(parsed, 'mm'),
      ampm: this.useTwelveHoursTimeFormat ? format(parsed, 'aa') : '',
    };
  }

  private getTimeValueToDisplay(
    value: string,
    hour12 = this.useTwelveHoursTimeFormat,
  ): string | null {
    if (!regexpMatch(timeRegex, value)) {
      return null;
    }

    return removeSeconds(timeFormatter(value, hour12)) || null;
  }

  private createTimeFormControl(): FormControl {
    const value = this.getTimeValueToDisplay(this.value() as string);
    const formControl = new FormControl({
      value: value,
      disabled: this.isDisabled(),
    });

    formControl.valueChanges.pipe(untilDestroyed(this)).subscribe((value) => {
      this.filterString$.next(value || '');
      const newTimeParts = this.getTimeParts(value);
      if (!newTimeParts) {
        this.valueChanged.emit(null);
        return;
      }
      this.valueChanged.emit(timeFormatter(value, false) ?? null);
    });

    return formControl;
  }
}
